import { createRef, Component } from 'react'
import * as cn from 'classnames'
import InsightFooter from 'components/features/insights/InsightFooter/InsightFooterContainer'
import { Insight as InsightInterface, InsightBorderColor, InsightStyle, InsightTag } from 'interfaces/insights'
import Shownable from 'components/utils/Shownable'
import Scrollable from 'components/atoms/Scrollable'
import {
  isHighlightInsight,
  isModuleInsight,
  isOpportunityInsight,
  isOpportunityPageInsight,
  isServiceInsight,
  isCoachingCarousel,
} from 'helpers/insightHelpers'
import { insightShownActionCreator } from 'actions/insightsActions'
import { formatRewardAmount } from 'helpers/formatHelpers'

import Annotation from 'components/features/insights/Insight/Annotation'
import Badge from 'components/features/insights/Insight/Badge'

import * as styles from 'components/features/insights/Insight/Insight.css'
import ReactSvgIcon from 'icons/*.svg'
import RewardableSvg from 'icons/rewardable.svg'
import WarningSvg from 'icons/warning.svg'
import SuccessSvg from 'icons/success.svg'
import { ExpandableAnnotation } from 'components/features/pfm/ExpandableAnnotation/ExpandableAnnotation'
import { filterEventsFromNode, getOffset, RectOffset } from 'helpers/DOMHelpers'
import { Markdown } from 'components/features/pfm/Markdown/Markdown'
import { defaultMemoize } from 'reselect'
import { sendWarning } from 'helpers/insightMarkdownHelper'
import { SocialInnerInsight } from 'interfaces/socialInsights'

export const COPY_RATE_MATCH_SUFFIX = '% Match'

const BADGE_ICON_BY_STATUS: Partial<Record<InsightStyle['status'], typeof ReactSvgIcon>> = {
  success: SuccessSvg,
  warning: WarningSvg,
  alert: WarningSvg,
}

const BORDER_COLOR_BY_STATUS: Record<InsightStyle['status'], InsightBorderColor> = {
  default: 'none',
  success: 'green',
  info: 'blue',
  warning: 'orange',
  alert: 'red',
}

type InsightType = InsightInterface | SocialInnerInsight
export type InsightProps = {
  insight: InsightType
  hasNext?: boolean
  notTouchable?: boolean
  category?: string
  noCard?: boolean
  // null in case of fake insight
  onShown?: typeof insightShownActionCreator
}

type InsightState = {
  isMatchRateExpanded: boolean
  isRewardExpanded: boolean
}

class Insight extends Component<InsightProps, InsightState> {
  state = {
    isMatchRateExpanded: false,
    isRewardExpanded: false,
  }

  private rewardBadgeRef = createRef<HTMLDivElement>()
  private matchBadgeRef = createRef<HTMLDivElement>()

  render () {
    const { insight, notTouchable, onShown, noCard } = this.props
    const { slug, style } = insight
    const borderColor = style && style.status
      ? BORDER_COLOR_BY_STATUS[style.status]
      : 'none'

    const className = cn(styles.insight, {
      [styles.isHighlight]: isHighlightInsight(insight),
      [styles.isPage]: isOpportunityPageInsight(insight),
      [styles.isModule]: isModuleInsight(insight),
      [styles.noCard]: noCard,
    })

    const bodyClassName = cn(styles.body, {
      [styles[borderColor]]: isModuleInsight(insight),
      [styles.notTouchable]: notTouchable,
    })

    return (
      <div className={ className }>
        { this.renderSponsoredLabel() }
        <div className={ bodyClassName } data-slug={ slug }>
          { this.renderHeader() }
          { this.renderCategory() }
          <Shownable
            onShown={ this.handleShown }
            isShownEnabled={ !!onShown }
          >
            { this.renderScrollableContent() }
          </Shownable>
          { this.renderFooter() }
        </div>
        { this.renderMatchRateAnnotation() }
        { this.renderRewardAnnotation() }
      </div>
    )
  }

  renderHeader () {
    const { tags = [] } = this.props.insight
    const leftBadgeChildren = [
      ...tags.map(this.renderTagBadge),
      this.renderMatchRateBadge(),
      this.renderRewardBadge(),
    ].filter(Boolean)
    const rightBadgeChildren = [
      this.renderNewBadge(),
    ].filter(Boolean)

    if (!leftBadgeChildren.length && !rightBadgeChildren.length) {
      return null
    }

    return (
      <header className={ styles.header }>
        <div className={ styles.leftBadgeWrapper }>
          { leftBadgeChildren }
        </div>
        <div className={ styles.rightBadgeWrapper }>
          { rightBadgeChildren }
        </div>
      </header>
    )
  }

  renderCategory () {
    const { category } = this.props
    if (!category) {
      return null
    }

    return (
      <h6 className={ styles.category }>
        { category }
      </h6>
    )
  }

  renderNewBadge () {
    const { insight } = this.props
    if (!isOpportunityPageInsight(insight)) {
      return null
    }
    if (isServiceInsight(insight)) {
      return null
    }
    if (!insight.is_new || insight.is_dismissed) {
      return null
    }

    return (
      <Badge theme='fresh' key='new'>
        New
      </Badge>
    )
  }

  renderRewardAnnotation () {
    const { insight } = this.props
    const { reward } = insight
    if (!this.shouldDisplayReward() || !reward) {
      return null
    }

    const { text } = reward
    if (!text) {
      return null
    }

    const rewardBadgeNode = this.rewardBadgeRef.current
    let offset: RectOffset | null = null
    if (rewardBadgeNode) {
      offset = getOffset(rewardBadgeNode.parentNode as HTMLElement, rewardBadgeNode)
    }

    return (
      <ExpandableAnnotation
        isShown={ this.state.isRewardExpanded }
        offset={ offset }
        onClose={ filterEventsFromNode(this.rewardBadgeRef, this.closeAnnotation) }
      >
        <Annotation
          theme='golden'
          size={ isHighlightInsight(insight) ? 'fixed' : 'responsive' }
          isExpanded={ this.state.isRewardExpanded }
          isModuleInsight={ isModuleInsight(insight) }
          title='Reward Details'
          markdown={ text }
          onCloseButtonClick={ this.closeAnnotation }
        />
      </ExpandableAnnotation>
    )
  }

  renderMatchRateAnnotation () {
    const { insight } = this.props
    const { match } = insight
    if (!this.shouldDisplayMatchRate() || !match) {
      return null
    }

    const { text } = match
    if (!text) {
      return null
    }

    const matchBadgeNode = this.matchBadgeRef.current
    let offset: RectOffset | null = null
    if (matchBadgeNode) {
      offset = getOffset(matchBadgeNode.parentNode as HTMLElement, matchBadgeNode)
    }

    return (
      <ExpandableAnnotation
        isShown={ this.state.isMatchRateExpanded }
        offset={ offset }
        onClose={ filterEventsFromNode(this.matchBadgeRef, this.closeAnnotation) }
      >
        <Annotation
          theme='blue'
          isExpanded={ this.state.isMatchRateExpanded }
          isModuleInsight={ isModuleInsight(insight) }
          title='Match Details'
          markdown={ text }
          onCloseButtonClick={ this.handleAnnotationCloseButtonClick }
        />
      </ExpandableAnnotation>
    )
  }

  renderRewardBadge () {
    const { insight } = this.props
    const { reward } = insight
    if (!this.shouldDisplayReward() || !reward) {
      return null
    }

    const icon = isHighlightInsight(insight) ? RewardableSvg : undefined
    const amount = formatRewardAmount(reward)

    return (
      <div
        key='reward'
        ref={ this.rewardBadgeRef }
      >
        <Badge
          theme='golden'
          icon={ icon }
          withChevron={ Boolean(reward.text) }
          isExpanded={ this.state.isRewardExpanded }
          isFaded={ this.state.isMatchRateExpanded }
          onClick={ this.handleRewardBadgeClick }
        >
          { amount } Reward
        </Badge>
      </div>
    )
  }

  renderMatchRateBadge () {
    const { match } = this.props.insight

    if (!this.shouldDisplayMatchRate() || !match) {
      return null
    }

    return (
      <div
        key='match-rate'
        ref={ this.matchBadgeRef }
      >
        <Badge
          theme='blue'
          withChevron={ Boolean(match && match.text) }
          isExpanded={ this.state.isMatchRateExpanded }
          isFaded={ this.state.isRewardExpanded }
          onClick={ this.handleMatchRateBadgeClick }
        >
          { match.value }{ COPY_RATE_MATCH_SUFFIX }
        </Badge>
      </div>
    )
  }

  renderScrollableContent () {
    const { insight } = this.props
    const title = this.renderTitle()
    const scrollableWrapperClassName = cn(styles.scrollableWrapper, {
      [styles.notScrollable]: isOpportunityPageInsight(insight) || isCoachingCarousel(insight),
      [styles.hasNoTitle]: !title,
    })

    return (
      <div className={ scrollableWrapperClassName }>
        <Scrollable
          alwaysShowScrollbar
          shouldFade
          allowOuterScroll
          contentClassName={ styles.content }
        >
          { title }
          { this.renderContent(insight) }
        </Scrollable>
      </div>
    )
  }

  renderTagBadge = ({ text, status }: InsightTag, index: number) => {
    const { insight } = this.props
    if (!text) {
      return null
    }
    if (isHighlightInsight(insight) && index >= (this.shouldDisplayReward() ? 1 : 2)) {
      return null
    }

    const { isRewardExpanded, isMatchRateExpanded } = this.state
    const isFaded = isRewardExpanded || isMatchRateExpanded
    const theme = status === 'default' ? 'gray' : status
    const icon = isHighlightInsight(insight)
      ? BADGE_ICON_BY_STATUS[status]
      : undefined

    return (
      <Badge
        theme={ theme }
        icon={ icon }
        isFaded={ isFaded }
        key={ String(index) }
      >
        { text }
      </Badge>
    )
  }

  renderTitle () {
    const { insight } = this.props
    if (isOpportunityPageInsight(insight)) {
      return null
    }
    if (!insight.title) {
      return null
    }

    return (
      <div className={ styles.title }>
        { insight.title }
      </div>
    )
  }

  renderSponsoredLabel () {
    const { insight } = this.props
    if (!insight.is_sponsored) {
      return null
    }
    if (!isOpportunityInsight(insight)) {
      return null
    }

    return (
      <div className={ styles.sponsoredLabel }>
        Sponsored
      </div>
    )
  }

  renderFooter () {
    const { insight, hasNext } = this.props
    const { cta } = insight
    if (!cta) {
      return null
    }

    const className = cn(styles.footer)

    return (
      <div className={ className }>
        <InsightFooter
          insight={ insight }
          hasNext={ hasNext }
        />
      </div>
    )
  }

  private renderContent = defaultMemoize((insight: InsightType) => {
    const isOpportunity = isOpportunityInsight(insight)
    return (
      <section className={ styles.text }>
        { !isOpportunity && insight.text }
        { isOpportunity && (
          <Markdown text={ insight.text } onError={ (tokenType) => sendWarning(insight, tokenType) } />
        )}
      </section>
    )
  })

  private shouldDisplayMatchRate () {
    const { insight } = this.props
    if (isServiceInsight(insight)) {
      return false
    }

    if (!isOpportunityPageInsight(insight)) {
      return false
    }

    return insight.match
  }

  private shouldDisplayReward () {
    const { insight } = this.props
    const { reward } = insight
    return !!reward && reward.value
  }

  private closeAnnotation = () => {
    this.setState({
      isMatchRateExpanded: false,
      isRewardExpanded: false,
    })
  }

  private handleMatchRateBadgeClick = () => {
    const { match } = this.props.insight
    const isMatchRateExpanded = match && match.text
      ? !this.state.isMatchRateExpanded
      : false

    this.setState({
      isMatchRateExpanded,
      isRewardExpanded: false,
    })
  }

  private handleRewardBadgeClick = () => {
    const { reward } = this.props.insight
    const isRewardExpanded = reward && reward.text
      ? !this.state.isRewardExpanded
      : false

    this.setState({
      isMatchRateExpanded: false,
      isRewardExpanded,
    })
  }

  private handleShown = () => {
    const { onShown, insight } = this.props

    if (onShown) {
      onShown(insight)
    }
  }

  private handleAnnotationCloseButtonClick = () => {
    this.closeAnnotation()
  }
}

export default Insight
